/**
 * Shared “no OMS order for this invoice” guidance.
 * Same two-path message across Match modal, Matching column, and Match queue.
 *
 * Credit notes use different copy: OCR `po_not_found` is often a wrong invoice
 * ref (e.g. Brakes “With Reference To”), not a GR short-delivery mismatch.
 */

import { isCreditNoteDocument } from './creditNoteDocumentHelpers'

export const NO_ORDER_GUIDANCE_TITLE = 'No OMS order matches this invoice'

export const NO_ORDER_GUIDANCE_PATHS = [
  {
    key: 'in_oms',
    label: 'Ordered in OMS',
    text: 'but totals or delivery don’t line up — check the saved receipt in Goods receipting. Correct a data-entry mistake there; if the receipt is correct, keep it unchanged and use Review difference on the invoice to request credit.',
    linkKey: 'receipting',
    linkLabel: 'Goods receipting',
  },
  {
    key: 'outside_oms',
    label: 'Ordered outside OMS',
    text: '— use Create order from invoice in Actions (auto-links the PO), or add a manual order on Orders the usual way, then Match.',
    linkKey: 'ordersManual',
    linkLabel: 'Add manual order',
  },
]

/**
 * Apply-modal callout when a credit note’s PO / OCR ref didn’t resolve in OMS.
 * Deliberately avoids invoice “totals / delivery don’t line up” language.
 */
export const CN_PO_NOT_FOUND_GUIDANCE_TITLE =
  'No OMS purchase order matches this credit note'

export const CN_PO_NOT_FOUND_GUIDANCE_PATHS = [
  {
    key: 'pick_oms_po',
    label: 'Pick the right OMS PO',
    text: '— search below and Apply this credit to the purchase order it belongs to. Credit notes often show an invoice reference (“With Reference To”), not your OMS PO.',
    linkKey: null,
    linkLabel: null,
  },
  {
    key: 'outside_oms',
    label: 'Original order outside OMS',
    text: '— add a manual order on Orders first, then return here to Apply.',
    linkKey: 'ordersManual',
    linkLabel: 'Add manual order',
  },
]

/** Short line under the Matching badge (No match / missing PO). */
export const NO_ORDER_COLUMN_TIP =
  'No OMS order? Fix on Goods receipting, Create order from invoice in Actions, or add a manual order.'

/**
 * Blank Order No / missing PO (Mark Murphy–style PDFs with no PO field).
 * Stronger than the generic “no order” tip.
 */
export const BLANK_PO_COLUMN_TIP =
  'No Order No on this invoice — add the OMS PO, or pick an order in Match.'

export const BLANK_PO_MATCH_GUIDANCE_TITLE =
  'This invoice has no Order No / purchase order'

export const BLANK_PO_MATCH_GUIDANCE_BODY =
  'Some supplier PDFs leave Order No blank. Enter the OMS PO via Fix details / Edit invoice, or choose the correct order below — matching can’t run automatically until a PO is set.'

export const CN_BLANK_PO_GUIDANCE_TITLE =
  'This credit note has no Order No / purchase order'

export const CN_BLANK_PO_GUIDANCE_BODY =
  'Search for the OMS PO below and Apply — we can’t auto-apply until a purchase order is selected. Don’t create a new order for a credit.'

export const CN_PO_NOT_FOUND_COLUMN_TIP =
  'Wrong or missing PO on this credit note — open Apply and pick the correct OMS PO.'

/** Tip above the Match queue table when No match / Needs review is active. */
export const NO_ORDER_QUEUE_TIP =
  'Can’t find an order? Create order from invoice in Actions, add a manual order on Orders, or fix delivery on Goods receipting — then Match again.'

/** One-liner for the three-way match expandable guide. */
export const NO_ORDER_GUIDE_FOOT =
  'Invoice with no OMS order? Fix delivery on Goods receipting, Create order from invoice in Actions, or add a manual order on Orders — then Match.'

const MISSING_ORDER_FAILURE_REASONS = new Set([
  'po_not_found',
  'missing_po',
  'no_match',
])

export function isBlankPoMatchReason(invoice = {}) {
  return String(invoice?.matchingFailureReason || '').trim() === 'missing_po'
}

export function isCreditNotePoLookupFailure(invoice = {}) {
  return (
    isCreditNoteDocument(invoice) && shouldShowNoOrderMatchGuidance(invoice)
  )
}

/**
 * Match / Apply modal callout content — invoice vs credit-note specific.
 * @returns {{
 *   title: string,
 *   introBody: string|null,
 *   paths: Array<{ key: string, label: string, text: string, linkKey: string|null, linkLabel: string|null }>,
 * }}
 */
export function getNoOrderMatchGuidance(invoice = {}) {
  if (isCreditNoteDocument(invoice)) {
    if (isBlankPoMatchReason(invoice)) {
      return {
        title: CN_BLANK_PO_GUIDANCE_TITLE,
        introBody: CN_BLANK_PO_GUIDANCE_BODY,
        paths: CN_PO_NOT_FOUND_GUIDANCE_PATHS,
      }
    }
    return {
      title: CN_PO_NOT_FOUND_GUIDANCE_TITLE,
      introBody: null,
      paths: CN_PO_NOT_FOUND_GUIDANCE_PATHS,
    }
  }

  if (isBlankPoMatchReason(invoice)) {
    return {
      title: BLANK_PO_MATCH_GUIDANCE_TITLE,
      introBody: BLANK_PO_MATCH_GUIDANCE_BODY,
      paths: NO_ORDER_GUIDANCE_PATHS,
    }
  }

  return {
    title: NO_ORDER_GUIDANCE_TITLE,
    introBody: null,
    paths: NO_ORDER_GUIDANCE_PATHS,
  }
}

/** Column tip should not push Goods receipting for credit-note PO misses. */
export function shouldShowNoOrderColumnDeepLinks(invoice = {}) {
  return !isCreditNoteDocument(invoice)
}

/**
 * When to show the stronger “what to do next” callout (Match modal).
 */
export function shouldShowNoOrderMatchGuidance(invoice = {}) {
  const status = invoice?.matchingStatus || 'unprocessed'
  if (status === 'no_match') return true
  if (status === 'needs_action') {
    const reason = String(invoice?.matchingFailureReason || '').trim()
    if (MISSING_ORDER_FAILURE_REASONS.has(reason)) return true
    const hasPo = Boolean(
      String(invoice?.purchaseOrderNumber || '').trim() ||
        invoice?.linkedOrderId ||
        (Array.isArray(invoice?.matchedOrderIds) &&
          invoice.matchedOrderIds.length > 0),
    )
    return !hasPo
  }
  return false
}

/**
 * When to show the short Matching-column tip.
 */
export function shouldShowNoOrderColumnTip(invoice = {}) {
  // Already on a PO — tip about finding an order would be misleading.
  if ((invoice?.status || '') === 'attachedToPO') return false

  const status = invoice?.matchingStatus || 'unprocessed'
  if (status === 'no_match') return true
  if (status === 'needs_action') {
    const reason = String(invoice?.matchingFailureReason || '').trim()
    return MISSING_ORDER_FAILURE_REASONS.has(reason)
  }
  return false
}

/** Column tip text — blank PO and credit notes get specific copy. */
export function getNoOrderColumnTip(invoice = {}) {
  if (isCreditNoteDocument(invoice)) return CN_PO_NOT_FOUND_COLUMN_TIP
  if (isBlankPoMatchReason(invoice)) return BLANK_PO_COLUMN_TIP
  return NO_ORDER_COLUMN_TIP
}

/**
 * When to show the Match queue banner tip.
 */
export function shouldShowNoOrderQueueTip({
  matchingStatuses = [],
  isNeedsAttentionFilter = false,
} = {}) {
  if (isNeedsAttentionFilter) return true
  return (
    Array.isArray(matchingStatuses) && matchingStatuses.includes('no_match')
  )
}

export function buildPurchasingOrdersPath(
  venueId,
  { addManualOrder, invoiceId, po } = {},
) {
  const base = `/v/${venueId}/purchasing/orders`
  if (!addManualOrder) return base
  const params = new URLSearchParams({ addManualOrder: '1' })
  if (invoiceId) params.set('rematchInvoiceId', String(invoiceId))
  if (po) params.set('po', String(po).trim())
  return `${base}?${params.toString()}`
}

export function buildPurchasingReceiptingPath(venueId) {
  return `/v/${venueId}/purchasing/receipting`
}

/**
 * @param {string} venueId
 * @param {{ invoiceId?: string, purchaseOrderNumber?: string }} [invoiceContext]
 */
export function getNoOrderGuidanceLinks(venueId, invoiceContext = {}) {
  if (!venueId) {
    return { ordersManual: null, receipting: null }
  }
  return {
    ordersManual: buildPurchasingOrdersPath(venueId, {
      addManualOrder: true,
      invoiceId: invoiceContext.invoiceId,
      po: invoiceContext.purchaseOrderNumber,
    }),
    receipting: buildPurchasingReceiptingPath(venueId),
  }
}
